import { useMemo } from "react";
import { Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Banner mostrato SOLO nei 3 giorni finali del trial (esclusa la scadenza).
 *
 * Regole (spec):
 *   0 < giorni residui ≤ 3  → banner visibile con countdown
 *   giorni residui ≤ 0      → NIENTE banner ("sta per scadere" non ha senso quando è già scaduto)
 *                             il redirect a /abbonamento-bloccato è gestito dal middleware backend (HTTP 402)
 *   piano active / past_due → NIENTE banner (non c'è trial in scadenza)
 *
 * Fonte dati: usa esclusivamente `branding.billing_status.trial_ends_at` esposto
 * dal backend, senza ricalcolare valori discordanti nel frontend.
 */
export default function TrialCountdownBanner() {
  const { branding } = useAuth();
  const nav = useNavigate();
  const bs = branding?.billing_status;

  const info = useMemo(() => {
    if (!bs || bs.plan_status !== "trialing") return null;
    if (bs.trial_expired) return null;             // già scaduto → redirect gestisce
    const trialEndIso = bs.trial_ends_at;
    if (!trialEndIso) return null;
    const end = new Date(trialEndIso);
    if (Number.isNaN(end.getTime())) return null;
    // giorni residui interi (arrotondati per eccesso per essere "user-friendly":
    // se restano 27 ore mostriamo "2 giorni")
    const msLeft = end.getTime() - Date.now();
    if (msLeft <= 0) return null;                   // 0 o negativo → niente banner
    const daysLeft = Math.ceil(msLeft / 86_400_000);
    if (daysLeft > 3) return null;                  // solo negli ultimi 3 giorni
    return { daysLeft, end };
  }, [bs]);

  if (!info) return null;

  const label = info.daysLeft === 1
    ? "Il periodo di prova scade domani"
    : info.daysLeft === 0
      ? "Il periodo di prova termina oggi"      // fallback di sicurezza (in realtà non dovremmo mai arrivarci: > 0)
      : `Il periodo di prova scade tra ${info.daysLeft} giorni`;

  return (
    <div
      data-testid="trial-countdown-banner"
      className="w-full bg-amber-50 border-b border-amber-200"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3">
        <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <div className="flex-1 text-sm text-amber-900 leading-snug">
          <span className="font-semibold">{label}.</span>{" "}
          Attiva l&apos;abbonamento per non perdere l&apos;accesso alle funzionalità operative.
        </div>
        <Button
          size="sm"
          data-testid="trial-countdown-cta"
          onClick={() => nav("/admin/abbonamento")}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          Attiva ora
        </Button>
      </div>
    </div>
  );
}
